import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './NavigationBar.css';
import { fetchLawUpdates, updateNewsFlash, dismissNewsFlash } from '../services/lawUpdates';
import lgbtLogo from '../assets/lgbtpridepath.png';
import NewsFlashAdmin from './NewsFlashAdmin';

// Logo Component with image
const LGBTNearMeLogo = () => {
  return (
    <div className="lgbt-logo-container">
      <div className="logo-content-wrapper">
        <img 
          src={lgbtLogo}
          alt="LGBT Pride Path"
          className="lgbt-logo-image"
        />
        <div className="logo-text-wrapper">
          <span className="logo-text-finding">finding home</span>
          <span className="logo-text-program">a program of the lgbt national help center</span>
        </div>
      </div>
    </div>
  );
};

const NavigationBar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isMobileAboutOpen, setIsMobileAboutOpen] = useState(false);
  const [lawUpdates, setLawUpdates] = useState([]);
  const [showNewsFlash, setShowNewsFlash] = useState(false);
  const [dismissedUpdates, setDismissedUpdates] = useState([]);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const clickTimerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsAboutDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch law updates and reset dismissed updates on load
  useEffect(() => {
    localStorage.removeItem('dismissedLawUpdates');
    setDismissedUpdates([]);

    const fetchUpdates = async () => {
      try {
        const updates = await fetchLawUpdates();
        setLawUpdates(updates);
        setShowNewsFlash(updates.length > 0);
      } catch (error) {
        console.error('Error fetching law updates:', error);
      }
    };
    fetchUpdates();
  }, []);

  const handleDismissNewsFlash = async (updateId) => {
    try {
      await dismissNewsFlash(updateId);
      setDismissedUpdates([...dismissedUpdates, updateId]);
      setLawUpdates(lawUpdates.filter((update) => update.id !== updateId));
      if (lawUpdates.length <= 1) setShowNewsFlash(false);
    } catch (error) {
      console.error('Error dismissing news flash:', error);
    }
  };

  // Handle triple click on news flash
  const handleNewsFlashClick = () => {
    setClickCount(prev => prev + 1);
    
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    if (clickCount === 2) {
      setShowAdminModal(true);
      setClickCount(0);
    } else {
      clickTimerRef.current = setTimeout(() => {
        setClickCount(0);
      }, 500);
    }
  };

  // Handle news flash update from admin
  const handleUpdateNewsFlash = async (newText, credentials) => {
    try {
      console.log('Updating news flash with:', newText);
      const result = await updateNewsFlash({ 
        id: Date.now(), 
        lawName: newText,
        state: '', 
      }, credentials);
      
      console.log('Update result:', result);
      const updates = await fetchLawUpdates();
      setLawUpdates(updates);
      setShowNewsFlash(updates.length > 0);
      setDismissedUpdates([]);
      setShowAdminModal(false);
    } catch (error) {
      console.error('Error updating news flash:', error);
      throw error;
    }
  };

  const getCurrentNewsText = () => {
    const activeUpdates = lawUpdates.filter(update => !dismissedUpdates.includes(update.id));
    if (activeUpdates.length > 0) {
      const update = activeUpdates[0];
      return update.lawName;
    }
    return '';
  };

  return (
    <div className="navbar-wrapper">
      <nav className={`navbar lgbt-style ${isScrolled ? 'navbar-scrolled' : ''}`}>
        <div className="navbar-outer-container">
          <div className="navbar-top-section">
            <div className="navbar-logo">
              <Link to="/" className="logo-link lgbt-logo-link">
                <LGBTNearMeLogo />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="navbar-links-desktop lgbt-nav-links">
              <Link to="/" className="nav-link lgbt-nav-link">HOME</Link>
              
              {/* About Dropdown */}
              <div 
                className="nav-dropdown-wrapper" 
                ref={dropdownRef}
                onMouseEnter={() => setIsAboutDropdownOpen(true)}
                onMouseLeave={() => setIsAboutDropdownOpen(false)}
              >
                <span className="nav-link lgbt-nav-link dropdown-trigger">
                  ABOUT
                </span>
                <div className={`dropdown-menu ${isAboutDropdownOpen ? 'open' : ''}`}>
                  <Link 
                    to="/about" 
                    className="dropdown-item"
                    onClick={() => setIsAboutDropdownOpen(false)}
                  >
                    About Us
                  </Link>
                  <Link 
                    to="/methodology" 
                    className="dropdown-item"
                    onClick={() => setIsAboutDropdownOpen(false)}
                  >
                    Methodology
                  </Link>
                  <Link 
                    to="/media-tool-kit" 
                    className="dropdown-item"
                    onClick={() => setIsAboutDropdownOpen(false)}
                  >
                    Media Tool Kit
                  </Link>
                  <Link 
                    to="/contact" 
                    className="dropdown-item"
                    onClick={() => setIsAboutDropdownOpen(false)}
                  >
                    Contact
                  </Link>
                </div>
              </div>

              <Link to="/programs" className="nav-link lgbt-nav-link">PROGRAMS</Link>
              <Link to="https://www.lgbtnearme.org/" className="nav-link lgbt-nav-link" style={{ textTransform: 'none' }}>LGBTnearMe.org</Link>
              <Link to="https://givebutter.com/lgbthotline" className="nav-link lgbt-nav-link">DONATE</Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="mobile-menu-button lgbt-mobile-button" 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <div className={`menu-icon ${isMobileMenuOpen ? 'open' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`navbar-links-mobile lgbt-mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          <Link 
            to="/" 
            className="mobile-nav-link lgbt-mobile-nav-link"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            HOME
          </Link>
          
          {/* Mobile About with Submenu */}
          <div className="mobile-nav-dropdown">
            <button 
              className="mobile-nav-link lgbt-mobile-nav-link dropdown-mobile-trigger"
              onClick={() => setIsMobileAboutOpen(!isMobileAboutOpen)}
              aria-expanded={isMobileAboutOpen}
            >
              ABOUT
              <span className={`dropdown-arrow-mobile ${isMobileAboutOpen ? 'open' : ''}`}>▼</span>
            </button>
            <div className={`mobile-submenu ${isMobileAboutOpen ? 'open' : ''}`}>
              <Link 
                to="/about" 
                className="mobile-submenu-link"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsMobileAboutOpen(false);
                }}
              >
                About Us
              </Link>
              <Link 
                to="/methodology" 
                className="mobile-submenu-link"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsMobileAboutOpen(false);
                }}
              >
                Methodology
              </Link>
              <Link 
                to="/media-tool-kit" 
                className="mobile-submenu-link"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsMobileAboutOpen(false);
                }}
              >
                Media Tool Kit
              </Link>
              <Link 
                to="/contact" 
                className="mobile-submenu-link"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsMobileAboutOpen(false);
                }}
              >
                Contact
              </Link>
            </div>
          </div>

          <Link 
            to="/programs" 
            className="mobile-nav-link lgbt-mobile-nav-link"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            PROGRAMS
          </Link>
          <Link 
            to="https://www.lgbtnearme.org/" 
            className="mobile-nav-link lgbt-mobile-nav-link"
            onClick={() => setIsMobileMenuOpen(false)}
            style={{ textTransform: 'none' }}
          >
            LGBT near Me
          </Link>
          <Link 
            to="https://givebutter.com/lgbthotline" 
            className="mobile-nav-link lgbt-mobile-nav-link"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            DONATE
          </Link>
        </div>
      </nav>

      {/* News Flash Banner */}
      {showNewsFlash && lawUpdates.length > 0 && (
        <div 
          className="news-flash lgbt-news-flash" 
          role="alert" 
          aria-live="polite"
          onClick={handleNewsFlashClick}
        >
          {lawUpdates
            .filter((update) => !dismissedUpdates.includes(update.id))
            .map((update) => (
              <div key={update.id} className="news-flash-item">
                <span className="news-flash-text">
                  {update.lawName}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDismissNewsFlash(update.id);
                  }}
                  className="news-flash-dismiss"
                  aria-label={`Dismiss ${update.lawName} update`}
                >
                  ✕
                </button>
              </div>
            ))}
        </div>
      )}

      {/* Admin Modal */}
      {showAdminModal && (
        <NewsFlashAdmin
          currentNewsText={getCurrentNewsText()}
          onUpdateNews={handleUpdateNewsFlash}
          onClose={() => setShowAdminModal(false)}
        />
      )}
    </div>
  );
};

export default NavigationBar;